import { rem, SystemStyleObject } from '@vimeo/bokeh';
import { Maybe } from '@vimeo/core/types/generic';

import { ESidebarAttach } from './ResponsiveSidebar.types';
import { createDomName } from '../../utils/dom/identifying';
import { inline } from '../../utils/general/lambda';

export const SWIPE_OFFSET_FOR_ACTION: number = 50;
export const SIDEBAR_COLLAPSED_SIZE: string = rem(80);
export const SIDEBAR_COLLAPSED_MINIFIED_SIZE = rem(64);
const SIDEBAR_EXPANDED_DEFAULT_SIZE: string = rem(420);
const SIDEBAR_TRANSITION_DURATION: number = 200;

export function createSidebarStyles(props: {
  id: string;
  className: string;
  zIndex?: number | string;
  attach: ESidebarAttach;
  isActive: boolean;
  isFullscreen: boolean;
  isPortrait: boolean;
  isMobile: boolean;
  isExtended: boolean;
  isMinified: boolean;
  isWithBorder?: boolean;
  isOrientationChanged?: boolean;
  maxSize?: Maybe<number>;
  backgroundColor?: string;
}): SystemStyleObject {
  const {
    className,
    zIndex,
    attach,
    isActive,
    isFullscreen,
    isPortrait,
    isExtended,
    isMinified,
    isWithBorder,
    isOrientationChanged,
    maxSize,
    backgroundColor,
  } = props;

  const isHorizontalLayering: boolean =
    attach === ESidebarAttach.TOP || attach === ESidebarAttach.BOTTOM;
  const isHorizontalOrPortraitLayering: boolean = isHorizontalLayering || isPortrait;

  return {
    zIndex: zIndex,
    ...inline(() => {
      switch (attach) {
        case ESidebarAttach.TOP:
          return createSidebarVerticalStyles(props as ISidebarProps, 'flex-start');
        case ESidebarAttach.BOTTOM:
          return createSidebarVerticalStyles(props as ISidebarProps, 'flex-end');
        case ESidebarAttach.RIGHT:
          return isPortrait
            ? createSidebarVerticalStyles(props as ISidebarProps, 'flex-end')
            : createSidebarHorizontalStyles(props as ISidebarProps, 'flex-end');
        case ESidebarAttach.LEFT:
          return isPortrait
            ? createSidebarVerticalStyles(props as ISidebarProps, 'flex-end')
            : createSidebarHorizontalStyles(props as ISidebarProps, 'flex-start');
      }
    }),

    [`& .${createDomName(className, 'content')}`]: {
      flexGrow: isActive ? 1 : 0,
      transition: isFullscreen ? `flex-grow ${SIDEBAR_TRANSITION_DURATION}ms ease` : 'none',
    },

    [`& .${createDomName(className, 'panel')}`]: {
      ...createBorderStyles(attach, isPortrait),
      background: backgroundColor || 'background',
      height: isHorizontalOrPortraitLayering
        ? isMinified
          ? SIDEBAR_COLLAPSED_MINIFIED_SIZE
          : SIDEBAR_COLLAPSED_SIZE
        : undefined,
      width: isHorizontalOrPortraitLayering
        ? '100%'
        : isMinified
          ? SIDEBAR_COLLAPSED_MINIFIED_SIZE
          : SIDEBAR_COLLAPSED_SIZE,
      flexDirection: isHorizontalOrPortraitLayering ? 'row' : 'column',
      justifyContent: isHorizontalOrPortraitLayering ? 'center' : 'flex-start',
    },

    [`& .${createDomName(className, 'panel-scroller')}`]: {
      flexDirection: isHorizontalOrPortraitLayering ? 'row' : 'column',
      alignItems: isHorizontalOrPortraitLayering ? undefined : 'center',
      overflowX: isHorizontalOrPortraitLayering ? 'auto' : undefined,
      overflowY: isHorizontalOrPortraitLayering ? undefined : 'auto',
    },

    [`& .${createDomName(className, 'content-wrapper')}`]: {
      ...(isWithBorder ? createBorderStyles(attach, isPortrait) : {}),
      ...createContentWrapperStyles({
        attach: attach,
        maxSize: maxSize,
        isFullscreen: isFullscreen,
        isPortrait: isPortrait,
        isMinified: isMinified,
        isExtended: isExtended,
        isOrientationChanged: isOrientationChanged,
      }),
    },
  };
}

export function createSidebarExpandedSize(
  fullwidth: boolean = false,
  maxSize: Maybe<number>,
  isExtended: boolean = false,
  isPortrait: boolean = false,
): string {
  if (fullwidth) {
    return '100%';
  }

  if (maxSize) {
    return isExtended ? (isPortrait ? '75vh' : '75vw') : rem(maxSize);
  }

  return isExtended ? (isPortrait ? '75vh' : '75vw') : SIDEBAR_EXPANDED_DEFAULT_SIZE;
}

function createSidebarContentSize(
  fullwidth: boolean = false,
  maxSize: Maybe<number>,
  isExtended: boolean = false,
  isPortrait: boolean = false,
  isMinified: boolean = false,
): string {
  return fullwidth
    ? '100%'
    : `calc(${createSidebarExpandedSize(fullwidth, maxSize, isExtended, isPortrait)} - ${
        isMinified ? SIDEBAR_COLLAPSED_MINIFIED_SIZE : SIDEBAR_COLLAPSED_SIZE
      })`;
}

interface ISidebarProps {
  attach: ESidebarAttach;
  isActive: boolean;
  isFullscreen: boolean;
  isPortrait: boolean;
  isExtended: boolean;
  isMobile: boolean;
  isMinified?: boolean;
  isWithBorder?: boolean;
  isOrientationChanged?: boolean;
  maxSize: Maybe<number>;
}

export function createSidebarVerticalStyles(
  {
    isActive,
    isMinified,
    isFullscreen,
    maxSize,
    isOrientationChanged,
    isExtended,
    isPortrait,
  }: ISidebarProps,
  justifyContent: string,
): SystemStyleObject {
  return {
    width: '100%',
    flexDirection: 'column',
    justifyContent: justifyContent,
    alignItems: 'center',
    flexGrow: isFullscreen ? 1 : 'unset',
    height: isActive
      ? createSidebarExpandedSize(isFullscreen, maxSize, isExtended, isPortrait)
      : isMinified
        ? SIDEBAR_COLLAPSED_MINIFIED_SIZE
        : SIDEBAR_COLLAPSED_SIZE,
    transition:
      isFullscreen || (isOrientationChanged && isActive)
        ? 'none'
        : `height ${SIDEBAR_TRANSITION_DURATION}ms ease`,
  };
}

export function createSidebarHorizontalStyles(
  {
    isActive,
    isMinified,
    isFullscreen,
    maxSize,
    isOrientationChanged,
    isExtended,
    isPortrait,
  }: ISidebarProps,
  justifyContent: string,
): SystemStyleObject {
  return {
    justifyContent: isFullscreen ? 'center' : justifyContent,
    width: isActive
      ? createSidebarExpandedSize(isFullscreen, maxSize, isExtended, isPortrait)
      : isMinified
        ? SIDEBAR_COLLAPSED_MINIFIED_SIZE
        : SIDEBAR_COLLAPSED_SIZE,
    transition:
      isFullscreen || isOrientationChanged ? 'none' : `width ${SIDEBAR_TRANSITION_DURATION}ms ease`,
  };
}

export function createBorderStyles(attach: ESidebarAttach, isPortrait: boolean): SystemStyleObject {
  let side: string = '';

  switch (attach) {
    case ESidebarAttach.TOP:
      side = 'bottom';
      break;
    case ESidebarAttach.BOTTOM:
      side = 'top';
      break;
    case ESidebarAttach.RIGHT:
      side = isPortrait ? 'top' : 'left';
      break;
    case ESidebarAttach.LEFT:
      side = isPortrait ? 'top' : 'right';
      break;
  }

  return {
    [`border-${side}`]: '1px solid',
    borderColor: 'stroke',
  };
}

export function createContentWrapperStyles({
  attach,
  maxSize,
  isFullscreen,
  isMinified,
  isPortrait,
  isExtended,
  isOrientationChanged,
}: {
  attach: ESidebarAttach;
  maxSize?: Maybe<number>;
  isFullscreen: boolean;
  isMinified: boolean;
  isPortrait: boolean;
  isExtended: boolean;
  isOrientationChanged?: boolean;
}): SystemStyleObject {
  if (attach === ESidebarAttach.TOP) {
    return {
      right: 0,
      bottom: 0,
      left: 0,
      height: createSidebarContentSize(isFullscreen, maxSize, isExtended, isPortrait, isMinified),
      transition: `height ${SIDEBAR_TRANSITION_DURATION}ms ease`,
    };
  } else {
    if (
      attach === ESidebarAttach.BOTTOM ||
      (attach === ESidebarAttach.RIGHT && isPortrait) ||
      (attach === ESidebarAttach.LEFT && isPortrait)
    ) {
      return {
        top: 0,
        right: 0,
        left: 0,
        height: createSidebarContentSize(isFullscreen, maxSize, isExtended, isPortrait, isMinified),
        transition: `height ${SIDEBAR_TRANSITION_DURATION}ms ease`,
      };
    } else {
      if (attach === ESidebarAttach.RIGHT) {
        return {
          top: 0,
          bottom: 0,
          left: 0,
          width: createSidebarContentSize(
            isFullscreen,
            maxSize,
            isExtended,
            isPortrait,
            isMinified,
          ),
          transition:
            isFullscreen || isOrientationChanged
              ? 'none'
              : `width ${SIDEBAR_TRANSITION_DURATION}ms ease`,
        };
      } else if (attach === ESidebarAttach.LEFT) {
        return {
          top: 0,
          right: 0,
          bottom: 0,
          width: createSidebarContentSize(
            isFullscreen,
            maxSize,
            isExtended,
            isPortrait,
            isMinified,
          ),
          transition:
            isFullscreen || isOrientationChanged
              ? 'none'
              : `width ${SIDEBAR_TRANSITION_DURATION}ms ease`,
        };
      }
    }
  }

  return {};
}

export function createExtendButtonStyles({
  isMobile,
  attach,
  isPortrait,
  isFullscreen,
}: {
  isMobile: boolean;
  attach: ESidebarAttach;
  isPortrait: boolean;
  isFullscreen: boolean;
}): SystemStyleObject {
  const display: string = isMobile && !isFullscreen ? 'flex' : 'none';

  if (attach === ESidebarAttach.TOP) {
    return {
      display,
      left: '50%',
      bottom: rem(-10),
      transform: 'translateX(-50%)',
    };
  } else if (
    attach === ESidebarAttach.BOTTOM ||
    (attach === ESidebarAttach.RIGHT && isPortrait) ||
    (attach === ESidebarAttach.LEFT && isPortrait)
  ) {
    return {
      display,
      top: rem(-10),
      left: '50%',
      transform: 'translateX(-50%)',
    };
  } else if (attach === ESidebarAttach.RIGHT) {
    return {
      display,
      top: '50%',
      left: rem(-30),
      transform: 'translateY(-50%) rotate(90deg)',
    };
  } else if (attach === ESidebarAttach.LEFT) {
    return {
      display,
      top: '50%',
      right: rem(-30),
      transform: 'translateY(-50%) rotate(90deg)',
    };
  }

  return {
    display,
  };
}
